/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';
import {observer} from 'mobx-react';

const fn = `RadioButtonGroup -> `;

/**************************************** TYPE DEFINITIONS ****************************************/
type DisplayText = string;

interface RadioButtonGroupProps {
    selected: string;
    options: {[idValue: string]: string};
    setSelected: (val: string) => void;
    groupName?: string;
    labelCssClassNames?: string;
    inputCssClassNames?: string;
    wrapperInlineCssClassNames?: React.CSSProperties;
    onKeyPress?: EventHandler;
}

/**
 * Used to ensure unique group names always created, by iterating on each
 * created radio button component
 */
let numGroups = 0;

/********************************************* EXPORT *********************************************/
/**
 * Container for radio buttons
 *
 * Renders radio button set based on object given to options prop, where the
 * 'id' / true 'value' (identifying the radio button itself as an ID) is the
 * key, and the display text shown beside the box is the value
 *
 * Example: {idVal1: `Display Text One`}
 */
export const RadioButtonGroup = observer(<O extends string | any>(props: RadioButtonGroupProps) => {
    const groupName = props.groupName || `radio_button_group_${(numGroups++).toString()}`;

    return (
        <form radioGroup={groupName}>
            <div style={props.wrapperInlineCssClassNames}>
                {Object.keys(props.options).map((optionVal, idx) => (
                    <label
                        key={`radioButtonLabel_${optionVal}_pos${idx}`}
                        className={cn(`block`, `mt-6 mb-6 pt-10 pb-5 pl-5`)}
                    >
                        <input
                            type="radio"
                            name={`${groupName}RadioButtonGroup`}
                            value={optionVal}
                            className={props.inputCssClassNames || ``}
                            onChange={event => {
                                props.setSelected(event.target[`value`]);
                            }}
                            key={`radioButtonItem_${groupName}_${optionVal}_pos${idx}`}
                            checked={props.selected === optionVal}
                            onKeyPress={(props as any).onKeyPress}
                        />
                        <span className={props.labelCssClassNames || cn(`ml-15`)}>
                            {props.options[optionVal].capitalize}
                        </span>
                    </label>
                ))}
            </div>
        </form>
    );
});

RadioButtonGroup.displayName = `RadioButtonGroup`;
